class PredictiveSearch extends HTMLElement {
  static cache = new Map();
  static cacheLimit = 40;
  static instanceCount = 0;

  constructor() {
    super();

    this.instanceId = `predictive-search-${++PredictiveSearch.instanceCount}`;
    this.abortController = null;
    this.requestId = 0;
    this.searchTerm = '';
    this.isOpen = false;
    this.isComposing = false;
    this.isConnectedToEvents = false;

    this.onInput = this.debounce(this.handleInput.bind(this), 250);
    this.onFormSubmit = this.handleFormSubmit.bind(this);
    this.onFocus = this.handleFocus.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onCompositionStart = () => {
      this.isComposing = true;
    };
    this.onCompositionEnd = (event) => {
      this.isComposing = false;
      this.handleInput(event);
    };
    this.onModalClosed = () => this.close();
  }

  connectedCallback() {
    if (this.isConnectedToEvents) return;

    this.input = this.querySelector('input[type="search"]');
    this.form = this.input?.form;
    this.searchScope = this.closest('.search-parent-js') || this;
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search]') ||
      this.searchScope.querySelector('[data-predictive-search]');
    this.statusElement =
      this.querySelector('.predictive-search-status') ||
      this.searchScope.querySelector('.predictive-search-status');

    if (!this.input || !this.form || !this.predictiveSearchResults || !this.statusElement) {
      console.error('Predictive search could not initialize because required markup is missing.');
      return;
    }

    this.ensureUniqueInputId();
    this.predictiveSearchResults
      .querySelector('.predictive-search__loading-state.hide')
      ?.classList.remove('hide');

    this.form.addEventListener('submit', this.onFormSubmit);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    this.input.addEventListener('compositionstart', this.onCompositionStart);
    this.input.addEventListener('compositionend', this.onCompositionEnd);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeydown);
    document.body.addEventListener('modalClosed', this.onModalClosed);
    this.isConnectedToEvents = true;
  }

  disconnectedCallback() {
    if (!this.isConnectedToEvents) return;

    this.form.removeEventListener('submit', this.onFormSubmit);
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('focus', this.onFocus);
    this.input.removeEventListener('compositionstart', this.onCompositionStart);
    this.input.removeEventListener('compositionend', this.onCompositionEnd);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeydown);
    document.body.removeEventListener('modalClosed', this.onModalClosed);
    this.onInput.cancel();
    window.clearTimeout(this.liveRegionTimeout);
    this.abortActiveRequest();
    this.isConnectedToEvents = false;
  }

  debounce(callback, wait) {
    let timeout;
    const debounced = (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
    debounced.cancel = () => window.clearTimeout(timeout);
    return debounced;
  }

  ensureUniqueInputId() {
    if (this.input.dataset.predictiveSearchOwner === this.instanceId) return;

    const currentId = this.input.id || 'Search';
    const baseId = this.input.dataset.predictiveSearchBaseId || currentId;
    const inputId = `${baseId}-${this.instanceId}`;
    const label = Array.from(this.querySelectorAll('label[for]')).find(
      (candidate) => candidate.getAttribute('for') === currentId
    );

    this.input.dataset.predictiveSearchBaseId = baseId;
    this.input.dataset.predictiveSearchOwner = this.instanceId;
    this.input.id = inputId;
    if (label) label.setAttribute('for', inputId);
  }

  getQuery() {
    return this.input.value.trim().replace(/\s+/g, ' ');
  }

  getSearchConfig(searchTerm) {
    const rawQuery = (searchTerm || '').trim().replace(/\s+/g, ' ');
    const includeUnavailable = rawQuery.startsWith('%');
    const queryWithoutOverride = includeUnavailable ? rawQuery.replace(/^%+/, '').trim() : rawQuery;
    // Keep a standalone "%" as the query, matching the theme's original stock search shortcut.
    const query = includeUnavailable && queryWithoutOverride ? queryWithoutOverride : rawQuery;

    return {
      query,
      unavailableMode: includeUnavailable ? 'last' : 'hide',
    };
  }

  getCacheKey(searchConfig) {
    return `${searchConfig.unavailableMode}\u0000${searchConfig.query.toLocaleLowerCase()}`;
  }

  isCurrentSearchConfig(searchConfig) {
    return this.currentSearchConfig?.query === searchConfig.query &&
      this.currentSearchConfig?.unavailableMode === searchConfig.unavailableMode;
  }

  getFullSearchQuery(searchConfig) {
    const query = searchConfig.query;
    const lastTerm = query.split(/\s+/).pop() || '';
    const canUsePrefixSearch = Array.from(lastTerm).length >= 3 &&
      /[\p{L}\p{N}]$/u.test(lastTerm);

    if (!canUsePrefixSearch || query.endsWith('*')) return query;
    return `${query}*`;
  }

  prepareFormSubmission() {
    const searchConfig = this.getSearchConfig(this.getQuery());
    if (!searchConfig.query) return null;

    // Keep the private "%" stock override out of the public search query.
    this.input.value = searchConfig.query;
    const unavailableInput = this.form.querySelector(
      '[data-search-unavailable-products], input[name="options[unavailable_products]"]'
    );
    if (unavailableInput) unavailableInput.value = searchConfig.unavailableMode;

    return searchConfig;
  }

  navigateToFullSearch(searchConfig) {
    const searchUrl = new URL(
      this.form.action || window.routes?.search_url || '/search',
      window.location.origin
    );
    const formData = new FormData(this.form);
    const params = new URLSearchParams(formData);

    params.set('q', this.getFullSearchQuery(searchConfig));
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', searchConfig.unavailableMode);
    searchUrl.search = params.toString();
    window.location.assign(searchUrl.toString());
  }

  handleInput(event) {
    if (this.isComposing || event?.isComposing) return;

    const searchConfig = this.getSearchConfig(this.getQuery());
    this.searchTerm = searchConfig.query;

    if (!searchConfig.query) {
      this.clearSearch();
      return;
    }

    this.getSearchResults(searchConfig);
  }

  handleFormSubmit(event) {
    this.onInput.cancel();
    const searchConfig = this.prepareFormSubmission();
    if (!searchConfig) {
      event.preventDefault();
      return;
    }

    // Never open a stale selection left from the previous debounced query.
    const selectedAction = this.isCurrentSearchConfig(searchConfig)
      ? this.getSelectedAction()
      : null;
    if (selectedAction) {
      event.preventDefault();
      selectedAction.click();
      return;
    }

    if (this.getFullSearchQuery(searchConfig) !== searchConfig.query) {
      event.preventDefault();
      this.navigateToFullSearch(searchConfig);
    }
  }

  handleFocus() {
    const searchConfig = this.getSearchConfig(this.getQuery());
    if (!searchConfig.query) return;

    if (!this.isCurrentSearchConfig(searchConfig)) {
      this.searchTerm = searchConfig.query;
      this.getSearchResults(searchConfig);
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchConfig);
    }
  }

  handleFocusOut() {
    window.setTimeout(() => {
      if (!this.searchScope.contains(document.activeElement)) this.close();
    });
  }

  handleKeydown(event) {
    if (this.isComposing || event.isComposing) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption(1);
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
        break;
      case 'Enter': {
        const selectedAction = this.getSelectedAction();
        if (this.isOpen && selectedAction) {
          event.preventDefault();
          selectedAction.click();
        } else if (this.getSearchConfig(this.getQuery()).query) {
          event.preventDefault();
          if (typeof this.form.requestSubmit === 'function') {
            this.form.requestSubmit();
          } else {
            this.form.querySelector('[type="submit"]')?.click();
          }
        }
        break;
      }
      default:
        break;
    }
  }

  getVisibleOptions() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter(
      (option) => option.offsetParent !== null && option.querySelector('a, button')
    );
  }

  getSelectedOption() {
    return this.predictiveSearchResults.querySelector('[role="option"][aria-selected="true"]');
  }

  getSelectedAction() {
    return this.getSelectedOption()?.querySelector('a, button') || null;
  }

  switchOption(step) {
    if (!this.isOpen) return;

    const options = this.getVisibleOptions();
    if (!options.length) return;

    const selectedOption = this.getSelectedOption();
    const selectedIndex = options.indexOf(selectedOption);
    const nextIndex = selectedIndex === -1
      ? (step > 0 ? 0 : options.length - 1)
      : (selectedIndex + step + options.length) % options.length;
    const nextOption = options[nextIndex];

    if (selectedOption) selectedOption.setAttribute('aria-selected', 'false');
    nextOption.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', nextOption.id);
    this.statusElement.textContent = '';
    nextOption.scrollIntoView?.({ block: 'nearest' });
  }

  abortActiveRequest() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
  }

  getSearchResults(searchConfig) {
    this.currentSearchConfig = searchConfig;
    const cacheKey = this.getCacheKey(searchConfig);
    const currentRequestId = ++this.requestId;

    this.setLiveRegionLoadingState();
    this.abortActiveRequest();

    const cachedMarkup = PredictiveSearch.cache.get(cacheKey);
    if (cachedMarkup) {
      this.renderSearchResults(cachedMarkup, currentRequestId);
      return;
    }

    const params = new URLSearchParams();
    params.set('q', searchConfig.query);
    params.set('resources[type]', 'product,query');
    params.set('resources[limit]', '5');
    params.set('resources[limit_scope]', 'each');
    params.set(
      'resources[options][fields]',
      'title,body,product_type,vendor,variants.title,variants.sku,variants.barcode,tag'
    );
    params.set('resources[options][prefix]', 'last');
    params.set('resources[options][unavailable_products]', searchConfig.unavailableMode);
    params.set('section_id', 'predictive-search');

    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const predictiveSearchUrl = window.routes?.predictive_search_url || '/search/suggest';

    fetch(`${predictiveSearchUrl}?${params.toString()}`, {
      signal,
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (currentRequestId !== this.requestId) return;

        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const section = responseDocument.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search response did not contain the expected section.');

        this.setCachedResult(cacheKey, section.innerHTML);
        this.renderSearchResults(section.innerHTML, currentRequestId);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || currentRequestId !== this.requestId) return;

        console.error(error);
        this.removeAttribute('results');
        this.close();
      })
      .finally(() => {
        if (currentRequestId === this.requestId) this.abortController = null;
      });
  }

  setCachedResult(cacheKey, markup) {
    if (PredictiveSearch.cache.has(cacheKey)) PredictiveSearch.cache.delete(cacheKey);
    PredictiveSearch.cache.set(cacheKey, markup);

    if (PredictiveSearch.cache.size > PredictiveSearch.cacheLimit) {
      PredictiveSearch.cache.delete(PredictiveSearch.cache.keys().next().value);
    }
  }

  renderSearchResults(resultsMarkup, requestId) {
    if (requestId !== this.requestId) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.namespaceResultIds();
    this.updateSearchForLink();
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  namespaceResultIds() {
    const idMap = new Map();

    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element, index) => {
      const originalId = element.id;
      const uniqueId = `${this.instanceId}-${originalId}-${index}`;
      if (!idMap.has(originalId)) idMap.set(originalId, uniqueId);
      element.id = uniqueId;
    });

    this.predictiveSearchResults
      .querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [aria-owns]')
      .forEach((element) => {
        ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach((attribute) => {
          if (!element.hasAttribute(attribute)) return;
          const ids = element
            .getAttribute(attribute)
            .split(/\s+/)
            .map((id) => idMap.get(id) || id)
            .join(' ');
          element.setAttribute(attribute, ids);
        });
      });

    const resultsListbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (resultsListbox) {
      this.input.setAttribute('aria-controls', resultsListbox.id);
      this.input.setAttribute('aria-owns', resultsListbox.id);
    }
    this.input.removeAttribute('aria-activedescendant');
  }

  updateSearchForLink() {
    if (!this.currentSearchConfig?.query) return;

    const searchUrl = window.routes?.search_url || '/search';
    const searchRoute = new URL(searchUrl, window.location.origin);
    const fullSearchQuery = this.getFullSearchQuery(this.currentSearchConfig);

    this.predictiveSearchResults.querySelectorAll('a[href]').forEach((link) => {
      const linkUrl = new URL(link.getAttribute('href'), window.location.origin);
      if (linkUrl.origin !== searchRoute.origin || linkUrl.pathname !== searchRoute.pathname) return;

      if (link.hasAttribute('data-search-for-link')) {
        linkUrl.searchParams.set('q', fullSearchQuery);
      }
      if (!linkUrl.searchParams.has('q')) return;

      linkUrl.searchParams.set('type', 'product');
      linkUrl.searchParams.set('options[prefix]', 'last');
      linkUrl.searchParams.set('options[unavailable_products]', this.currentSearchConfig.unavailableMode);
      link.href = linkUrl.toString();
    });
  }

  setLiveRegionLoadingState() {
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
    if (this.getAttribute('results') === 'true') this.open();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    window.clearTimeout(this.liveRegionTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    this.liveRegionTimeout = window.setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countValue = this.predictiveSearchResults.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    this.setLiveRegionText(countValue?.textContent?.trim() || '');
  }

  getResultsMaxHeight() {
    return Math.max(200, window.innerHeight - this.getBoundingClientRect().bottom - 24);
  }

  open() {
    if (!this.closest('.search-parent-js')) {
      this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close() {
    const selectedOption = this.getSelectedOption();
    if (selectedOption) selectedOption.setAttribute('aria-selected', 'false');

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }

  clearSearch() {
    this.searchTerm = '';
    this.currentSearchConfig = null;
    this.requestId += 1;
    this.abortActiveRequest();
    this.removeAttribute('results');
    this.close();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
